import { ArrowClockwise, ArrowCounterClockwise } from '@phosphor-icons/react/dist/ssr';

import { cn } from '@/utils';

import { formatAudioPlayerTime } from '../utils/formatAudioPlayerTime';
import { useAudioPlayer } from '../utils/useAudioPlayer';

import * as styles from './AudioPlayer.css';
import type { PlayerControlProps } from './AudioPlayer.type';
import PlayButton from './PlayButton';
import { PlaybackSpeedControl } from './PlaybackSpeedControl';
import useWaveformCanvas from './useWaveformCanvas';

export function WaveformPlayer({
  audioUrl,
  audioType = 'audio/mpeg',
  thumbnailUrl,
  title,
  duration: knownDuration,
  primaryColor,
  secondaryColor,
  backgroundColor,
  textColor,
  iconColor: iconColorProp,
  showThumbnail,
  showTitle,
  showRewind,
  showForward,
  rewindSeconds,
  forwardSeconds,
  playButtonSize,
  skipButtonSize,
  waveformHeight,
  waveform,
  onPlayAttempt,
  isPlaybackLocked,
  isPreview,
  audioElementId,
  playbackStorageKey,
}: PlayerControlProps) {
  const {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    playbackRate,
    setPlaybackRate,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  } = useAudioPlayer({ rewindSeconds, forwardSeconds, knownDuration, playbackStorageKey, enablePlaybackSpeed: true });

  const { canvasRef, isDecoding, handleCanvasClick, handleCanvasKeyDown } = useWaveformCanvas({
    audioUrl,
    title,
    waveform,
    progress,
    currentTime,
    duration,
    seekTo,
    primaryColor,
    secondaryColor,
  });

  const iconColor = iconColorProp || textColor || primaryColor;

  return (
    <div
      className={cn(styles.playerWrapper, isPlaybackLocked && 'pointer-events-none cursor-not-allowed')}
      style={{ backgroundColor }}
    >
      {showThumbnail && thumbnailUrl && <img src={thumbnailUrl} alt="" className={styles.thumbnail} />}

      <div className={styles.playerContent}>
        {(showTitle && title) || isPreview ? (
          <div className={styles.titleRow}>
            {showTitle && title ? (
              <span className={styles.title} style={{ color: textColor || undefined }} title={title}>
                {title}
              </span>
            ) : (
              <span className={styles.title} />
            )}
            {isPreview && (
              <span className={styles.previewBadge} style={{ color: textColor || undefined }}>
                Preview
              </span>
            )}
          </div>
        ) : null}

        <div className={styles.controlsRow}>
          {showRewind && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleRewind}
              disabled={!isReady}
              aria-label={`Rewind ${rewindSeconds} seconds`}
            >
              <ArrowCounterClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {rewindSeconds}
              </span>
            </button>
          )}

          {!isPlaybackLocked && (
            <PlayButton
              isReady={isReady}
              isPlaybackLocked={isPlaybackLocked}
              isPlaying={isPlaying}
              isBuffering={isBuffering}
              playButtonSize={playButtonSize}
              iconColor={iconColor}
              togglePlay={togglePlay}
              onPlayAttempt={onPlayAttempt}
            />
          )}

          {showForward && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleForward}
              disabled={!isReady}
              aria-label={`Forward ${forwardSeconds} seconds`}
            >
              <ArrowClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {forwardSeconds}
              </span>
            </button>
          )}

          <PlaybackSpeedControl value={playbackRate} onChange={setPlaybackRate} color={iconColor} />
        </div>

        <div className={styles.progressRow}>
          <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
            {formatAudioPlayerTime(currentTime)}
          </span>

          <div className={styles.waveformWrapper} style={{ height: waveformHeight }}>
            {isDecoding && <div className={styles.waveformLoading} style={{ height: waveformHeight }} />}
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: waveformHeight, display: 'block', cursor: 'pointer' }}
              onClick={handleCanvasClick}
              onKeyDown={handleCanvasKeyDown}
              role="slider"
              aria-valuenow={currentTime}
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-label="Seek audio"
              tabIndex={0}
            />
          </div>

          <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
            {formatAudioPlayerTime(duration)}
          </span>
        </div>
      </div>

      {audioUrl && (
        <audio ref={audioRef} id={audioElementId} preload={srcAttached ? 'metadata' : 'none'}>
          <source src={audioUrl} type={audioType} />
          <track kind="captions" src="" label="No captions available" />
        </audio>
      )}
    </div>
  );
}
